import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import Footer from '../components/Footer';
import CountUp from 'react-countup';
import {
  Package,
  Target,
  Users,
  Award,
  TrendingUp,
  Shield,
  Heart,
  Zap,
  Globe2,
  PhoneCall,
  ArrowRight
} from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To revolutionize logistics by making shipping accessible, affordable, and reliable for businesses of all sizes across India.'
    },
    {
      icon: Heart,
      title: 'Customer First',
      description: 'We put our customers at the heart of everything we do, ensuring their success is our success.'
    },
    {
      icon: Shield,
      title: 'Trust & Security',
      description: 'Building lasting relationships through transparency, reliability, and secure handling of every shipment.'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'Constantly evolving our platform with cutting-edge technology to deliver the best shipping experience.'
    }
  ];

  const achievements = [
    { icon: Package, end: 10000, suffix: '+', label: 'Shipments Delivered' },
    { icon: Users, end: 500, suffix: '+', label: 'Active Customers' },
    { icon: Award, end: 99.5, suffix: '%', decimals: 1, label: 'On-Time Delivery' },
    { icon: TrendingUp, end: 3, suffix: '+', label: 'Courier Partners' }
  ];

  // Scroll-triggered count-up: starts animating when section enters viewport
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-indigo-500 selection:text-white">
      <PublicNavbar />


      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-48 pb-20 overflow-hidden bg-slate-900 text-center">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-20 bg-gradient-to-b from-indigo-500 to-transparent blur-[100px] rounded-full pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <span className="px-3 py-1 bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-flex items-center gap-2">
            <Package className="w-4 h-4" /> Who We Are
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-emerald-400">Flywell</span>
          </h1>
          <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
            We're on a mission to simplify logistics for businesses across India.
            From startups to enterprise giants, we provide the intelligence and partnerships needed to ship smarter.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Our Story</h2>
            <div className="w-20 h-1.5 bg-indigo-600 mx-auto rounded-full"></div>
          </div>

          <div className="prose prose-lg max-w-none text-slate-600 space-y-6 text-xl leading-relaxed">
            <p>
              Flywell was born from a simple observation: shipping in India was hopelessly complicated,
              expensive, and fragmented. Independent businesses had to juggle multiple courier partners,
              deal with absolutely inconsistent pricing, and struggle with disjointed tracking systems.
            </p>
            <p>
              We set out to change that immediately. By building a unified, intelligent platform that seamlessly connects businesses
              with multiple tier-1 courier partners, we've made shipping as simple as a few clicks.
              Our technology handles all of the dark-room complexity — from courier allocation algorithms to RTO management — so you can focus entirely on growing your product line.
            </p>
            <p className="font-semibold text-slate-800">
              Today, we're proud to serve massive volumes across India, delivering thousands
              of successful shipments every single month. But we're barely scratching the surface of our roadmap. Our vision is to absolutely cement ourselves as
              India's most trusted logistics partner, making multi-channel fulfillment invisible to the brand yet magical for the buyer.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Our Values</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The internal principles that dictate every line of code we write and every package we route.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all group"
                >
                  <div className="w-14 h-14 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed font-medium">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section ref={sectionRef} className="py-24 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 tracking-tight">Our Impact by the Numbers</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-50 rounded-full mb-6">
                    <Icon className="w-10 h-10 text-emerald-500" />
                  </div>
                  <div className="text-4xl lg:text-5xl font-black text-slate-900 mb-2">
                    {hasAnimated ? (
                      <CountUp
                        start={0}
                        end={achievement.end}
                        duration={2.5}
                        decimals={achievement.decimals || 0}
                        suffix={achievement.suffix}
                        separator=","
                      />
                    ) : (
                      '0' + achievement.suffix
                    )}
                  </div>
                  <div className="text-slate-500 font-bold uppercase tracking-wider text-sm">{achievement.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-indigo-600 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight">
            Join Us on This Journey
          </h2>
          <p className="text-xl text-indigo-100 mb-10">
            Be part of India's logistics revolution and scale your brand today.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="px-10 py-5 bg-white text-indigo-600 rounded-xl hover:bg-slate-50 transition-all font-black text-xl shadow-2xl flex items-center justify-center gap-2 mx-auto group"
          >
            Get Started Today
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
